// The commitment dialog (spec 11 6.15.33, mock n05c; round 2 U2): switching a commitment add-on on (AWS Shield Advanced),
// or its automatic renewal (user decision 2026-11-03), needs the typed "<network> 1-year". The add-on stays off until
// the typed text matches and the primary button is pressed; Cancel leaves it off.
import { TriangleAlert } from 'lucide-react';
import { useId, useState } from 'react';
import type { CommitmentTerms } from '../../api/types';
import { Dialog } from '../../components/ui';
import { strings } from '../../strings';
import { dayWords } from './helpers';

const a = strings.networks.entryPoints.aws;
const g = strings.networks.entryPoints.gcp;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10" → "Oct 2026". */
function asOfWords(asOf: string): string {
  const [year, month] = asOf.split('-');
  return month ? `${MONTHS[Number(month) - 1] ?? month} ${year}` : asOf;
}

export function CommitmentDialog({
  network,
  label,
  terms,
  account,
  expected,
  variant = 'subscribe',
  endsAt,
  billingAccount = false,
  onConfirm,
  onCancel,
}: {
  network: string;
  label: string;
  terms: CommitmentTerms;
  /** The connectivity account's name (the payer account's member). */
  account: string | null;
  expected: string;
  variant?: 'subscribe' | 'renew';
  /** The current term's end (renewal), when known. */
  endsAt?: string | null;
  /**
   * Feature spec 17, phase 7: the subscription is the Google Cloud billing account's (Cloud Armor Enterprise Annual): a
   * billing administrator starts it in the console, Nebisys enrols the network's projects, and it has no renewal switch.
   */
  billingAccount?: boolean;
  onConfirm: (typed: string) => void;
  onCancel: () => void;
}) {
  const ids = useId();
  const [typed, setTyped] = useState('');
  const [tried, setTried] = useState(false);
  const matches = typed.trim() === expected;
  const renew = variant === 'renew';
  const termsId = `${ids}-terms`;
  const fieldId = `${ids}-typed`;
  const confirm = () => {
    if (!matches) {
      setTried(true);
      return;
    }
    onConfirm(typed.trim());
  };
  return (
    // Key events stay in this dialog (it opens over the entry point form).
    <div onKeyDown={(event) => event.stopPropagation()}>
      <Dialog
        title={renew ? a.renewTitle(network, label) : a.dialogTitle(network, label)}
        onClose={onCancel}
        className="net-commit-dialog"
        describedBy={termsId}
        footer={
          <>
            <button type="button" className="btn" onClick={onCancel}>
              {strings.common.cancel}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              aria-disabled={matches ? undefined : true}
              data-testid="commitment-confirm"
              onClick={confirm}
            >
              {renew ? a.renew : a.subscribe}
            </button>
          </>
        }
      >
        <div id={termsId} className="space-y-2 text-[13.5px]" data-testid="commitment-terms">
          {!billingAccount && (
            <p className="flex items-start gap-2">
              <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-[var(--danger)]" />
              <span>
                {a.bills} <strong>{a.perMonthFor(terms.currency, terms.monthly, terms.months)}</strong>{' '}
                {a.forPayer} <code className="font-mono text-[12.5px]">{account ?? terms.per}</code>{' '}
                {a.listPrice(asOfWords(terms.asOf))}
              </span>
            </p>
          )}
          {billingAccount ? (
            <>
              <p className="flex items-start gap-2">
                <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-[var(--danger)]" />
                <span>
                  {g.enrols(network, label)}{' '}
                  <strong>{a.perMonthFor(terms.currency, terms.monthly, terms.months)}</strong>{' '}
                  {g.enrolsTerms(terms.per, asOfWords(terms.asOf))}
                </span>
              </p>
              <p>
                <strong>{g.keepsBilling(terms.months)}</strong> {g.startsOnApply}
              </p>
              <p>{g.billingNote}</p>
            </>
          ) : renew ? (
            <p>
              <strong>{a.renewBody(network, label, terms.currency, terms.monthly, terms.months)}</strong>{' '}
              {a.renewOff(endsAt ? dayWords(endsAt) : null)}
            </p>
          ) : (
            <p>
              <strong>{a.keepsBilling(terms.months, network)}</strong> {a.startsOnApply}
            </p>
          )}
        </div>
        <div className="mt-3">
          <label className="label" htmlFor={fieldId}>
            {a.typeBefore} <code className="font-mono">{expected}</code>{' '}
            {renew ? a.typeAfterRenew : a.typeAfter}
          </label>
          <input
            id={fieldId}
            className="input w-full font-mono"
            value={typed}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={tried && !matches ? true : undefined}
            aria-describedby={tried && !matches ? `${fieldId}-err` : undefined}
            onChange={(event) => setTyped(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                confirm();
              }
            }}
          />
          {tried && !matches && (
            <p id={`${fieldId}-err`} className="mt-1 text-[12.5px] text-[var(--danger)]">
              {a.mismatch(expected)}
            </p>
          )}
        </div>
      </Dialog>
    </div>
  );
}
